import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, FileText, Download, Clock, PackageCheck, PhoneCall, ShoppingBag, Truck, Home, XCircle, MapPin } from 'lucide-react';
import { cn, formatDate, formatPrice, orderStatuses, itemMeasurements } from '@/lib/utils';
import { ACCOUNT } from '@/constants/testIds';
import ProductImage from '@/components/ProductImage';

// مراحل الطلب بالترتيب (الملغي خارجها)
const STEPS = [
  { key: 'pending', label: 'الاستلام', icon: Clock },
  { key: 'confirmed', label: 'التأكيد', icon: PhoneCall },
  { key: 'processing', label: 'التجهيز', icon: PackageCheck },
  { key: 'shipped', label: 'في الطريق', icon: Truck },
  { key: 'delivered', label: 'التسليم', icon: Home },
];

const BADGE = {
  pending: 'bg-amber-50 text-amber-700 ring-amber-200',
  confirmed: 'bg-sky-50 text-sky-700 ring-sky-200',
  processing: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  shipped: 'bg-violet-50 text-violet-700 ring-violet-200',
  delivered: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 ring-rose-200',
};

// شريط الحالات: دواير المراحل + خط تقدّم متدرّج
const StatusTracker = ({ status, cancelReason }) => {
  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-rose-50 border border-rose-100 px-4 py-3">
        <XCircle className="w-5 h-5 text-rose-600 shrink-0" />
        <p className="font-cairo text-sm text-rose-700">
          {cancelReason === 'إلغاء تلقائي لعدم التأكيد'
            ? 'تم إلغاء هذا الطلب تلقائياً لعدم تأكيده في الوقت المحدد. يسعدنا تطلبيه من جديد.'
            : 'تم إلغاء هذا الطلب. لأي استفسار تواصلي معنا عبر واتساب.'}
        </p>
      </div>
    );
  }

  const current = Math.max(0, STEPS.findIndex((s) => s.key === status));
  const progress = (current / (STEPS.length - 1)) * 100;

  return (
    <div className="relative px-1" aria-label={`حالة الطلب: ${orderStatuses[status]}`}>
      {/* الخط الخلفي + التقدّم (RTL: بيبدأ من اليمين) */}
      <div className="absolute top-4 right-5 left-5 h-1 rounded-full bg-gray-100" />
      <div
        className="absolute top-4 right-5 h-1 rounded-full bg-gradient-to-l from-burgundy-500 to-brand-gold transition-all duration-700"
        style={{ width: `calc((100% - 2.5rem) * ${progress / 100})` }}
      />
      <ol className="relative flex justify-between">
        {STEPS.map((step, i) => {
          const done = i < current;
          const active = i === current;
          const Icon = done ? Check : step.icon;
          return (
            <li key={step.key} className="flex flex-col items-center gap-1.5 w-12">
              <span
                className={cn(
                  'relative w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300',
                  done && 'bg-burgundy-500 text-white shadow-sm',
                  active && 'bg-white text-burgundy-500 ring-2 ring-burgundy-500 shadow-md shadow-burgundy-500/20',
                  !done && !active && 'bg-white text-gray-300 ring-1 ring-gray-200'
                )}
              >
                {active && step.key !== 'delivered' && (
                  <span className="absolute inset-0 rounded-full ring-2 ring-burgundy-500/40 animate-ping" />
                )}
                <Icon className="w-4 h-4" strokeWidth={done ? 3 : 2} />
              </span>
              <span
                className={cn(
                  'text-[11px] font-cairo whitespace-nowrap',
                  active ? 'text-burgundy-600 font-bold' : done ? 'text-gray-700 font-semibold' : 'text-gray-400'
                )}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

// خصائص التركيبة كشارات: دائرة اللون + باقي القيم (الطلبات القديمة ممكن يبقى فيها طول/وزن)
const OptionChips = ({ item }) => {
  // الطلبات القديمة ممكن يكون عندها النص بس
  const options = [
    ...(item.options?.length
      ? item.options
      : (item.options_label || '').split(' / ').filter(Boolean).map((value) => ({ value }))),
    ...itemMeasurements(item),
  ];
  if (!options.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {options.map((opt, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-gray-50 ring-1 ring-gray-200 text-[11px] font-cairo text-gray-700"
          title={opt.name ? `${opt.name}: ${opt.value}` : opt.value}
        >
          {opt.hex && <span className="w-3 h-3 rounded-full ring-1 ring-black/10 shrink-0" style={{ backgroundColor: opt.hex }} />}
          {opt.name && !opt.hex && <span className="text-gray-400">{opt.name}:</span>}
          <span className="font-semibold">{opt.value}</span>
        </span>
      ))}
    </div>
  );
};

// سطر منتج: الصورة + الاسم + الخصائص + السعر × الكمية
const ItemRow = ({ item }) => (
  <li className="flex gap-3 py-3 first:pt-0 last:pb-0">
    <div className="relative shrink-0">
      <ProductImage
        src={item.image_url}
        name={item.name_ar}
        className="w-16 h-20 rounded-xl object-cover ring-1 ring-gray-100 bg-gray-100"
        textClassName="text-[9px]"
      />
      {item.quantity > 1 && (
        <span className="absolute -top-1.5 -left-1.5 min-w-5 h-5 px-1 rounded-full bg-burgundy-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white">
          {item.quantity}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0 font-cairo">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-bold text-brand-black line-clamp-1">{item.name_ar}</p>
        <p className="text-sm font-bold text-burgundy-500 whitespace-nowrap">{formatPrice(item.line_total)}</p>
      </div>
      <OptionChips item={item} />
      <p className="text-[11px] text-gray-400 mt-1">{formatPrice(item.unit_price)} × {item.quantity}</p>
    </div>
  </li>
);

const VISIBLE_ITEMS = 2;

const OrderCard = ({ order }) => {
  const [open, setOpen] = useState(false);
  const pieces = order.items_count ?? order.items.reduce((sum, item) => sum + item.quantity, 0);
  const address = order.shipping_address;

  return (
    <article className="flex flex-col bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-lg hover:border-burgundy-500/30 transition-all duration-300 overflow-hidden">
      {/* الهيدر */}
      <div className="flex items-start justify-between gap-3 px-5 pt-5">
        <div className="min-w-0">
          <p className="text-xs text-gray-500 font-cairo">رقم الطلب</p>
          <p className="font-mono font-bold text-lg text-brand-black tracking-wide" dir="ltr" style={{ textAlign: 'right' }}>
            {order.order_number}
          </p>
          <p className="text-xs text-gray-500 font-cairo mt-0.5">{formatDate(order.created_at)}</p>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <span className={cn('px-3 py-1 rounded-full text-xs font-bold font-cairo ring-1', BADGE[order.status] || BADGE.pending)}>
            {orderStatuses[order.status] || order.status}
          </span>
          <div className="flex items-center gap-1.5">
            <Link
              to={`/orders/${order.order_number}/invoice`}
              title="عرض الفاتورة"
              aria-label="عرض الفاتورة"
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border border-gray-200 text-xs font-cairo font-semibold text-gray-600 hover:border-burgundy-500 hover:text-burgundy-500 transition"
            >
              <FileText className="w-3.5 h-3.5" /> الفاتورة
            </Link>
            <Link
              to={`/orders/${order.order_number}/invoice?download=1`}
              title="تحميل الفاتورة PDF"
              aria-label="تحميل الفاتورة PDF"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-burgundy-500 hover:border-burgundy-500 hover:text-white transition"
            >
              <Download className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* المنتجات */}
      <ul className="px-5 pt-4 pb-1 divide-y divide-gray-100">
        {order.items.slice(0, VISIBLE_ITEMS).map((item) => (
          <ItemRow key={item.id} item={item} />
        ))}
      </ul>
      {order.items.length > VISIBLE_ITEMS && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mx-5 mt-2 text-xs font-cairo font-semibold text-burgundy-500 hover:underline self-start"
        >
          + {order.items.length - VISIBLE_ITEMS} منتجات أخرى
        </button>
      )}

      {/* الإجمالي */}
      <div className="mx-5 mt-3 mb-4 flex items-center justify-between rounded-xl bg-gradient-to-l from-burgundy-500/[0.06] to-brand-gold/[0.08] px-4 py-2.5">
        <span className="text-xs text-gray-600 font-cairo">{pieces} قطعة · شامل التوصيل</span>
        <span className="font-bold text-lg text-burgundy-500 whitespace-nowrap">{formatPrice(order.total ?? order.total_amount)}</span>
      </div>

      {/* شريط الحالات */}
      <div className="px-5 pb-5">
        <StatusTracker status={order.status} cancelReason={order.cancel_reason} />
      </div>

      {/* التفاصيل */}
      <div className="mt-auto border-t border-gray-100">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full flex items-center justify-between px-5 py-3 text-sm font-cairo font-semibold text-gray-600 hover:text-burgundy-500 hover:bg-gray-50/80 transition-colors"
        >
          {open ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
          <ChevronDown className={cn('w-4 h-4 transition-transform duration-200', open && 'rotate-180')} />
        </button>

        <div className={cn('grid transition-all duration-300', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
          <div className="overflow-hidden">
            <div className="px-5 pb-5 space-y-3">
              {order.items.length > VISIBLE_ITEMS && (
                <ul className="divide-y divide-gray-100">
                  {order.items.slice(VISIBLE_ITEMS).map((item) => (
                    <ItemRow key={item.id} item={item} />
                  ))}
                </ul>
              )}

              <div className="space-y-1 text-sm font-cairo">
                <div className="flex justify-between text-gray-500"><span>المجموع الفرعي</span><span>{formatPrice(order.subtotal)}</span></div>
                <div className="flex justify-between text-gray-500"><span>رسوم التوصيل</span><span>{formatPrice(order.delivery_fee)}</span></div>
              </div>

              {address && (
                <p className="flex items-start gap-2 text-xs text-gray-500 font-cairo bg-gray-50 rounded-lg p-3">
                  <MapPin className="w-4 h-4 text-burgundy-500 shrink-0 mt-0.5" />
                  {[address.city, address.street, address.building].filter(Boolean).join('، ')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const OrdersTab = ({ orders }) => {
  if (orders.length === 0) {
    return (
      <div data-testid={ACCOUNT.ordersList} className="text-center bg-white rounded-2xl shadow-sm border border-gray-100 py-16 px-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-burgundy-500/10 flex items-center justify-center">
          <ShoppingBag className="w-8 h-8 text-burgundy-500" />
        </div>
        <p className="text-gray-700 font-tajawal font-bold text-lg mb-1">لا توجد طلبات بعد</p>
        <p className="text-gray-500 font-cairo text-sm mb-6">ابدئي التسوق واختاري ما يناسبك من تشكيلتنا</p>
        <Link to="/products" className="inline-flex items-center h-11 px-6 rounded-full bg-burgundy-500 hover:bg-burgundy-600 text-white font-cairo font-semibold transition">
          تسوّقي الآن
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={ACCOUNT.ordersList} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
      {orders.map((order) => (
        <OrderCard key={order.id} order={order} />
      ))}
    </div>
  );
};

export default OrdersTab;
